import { formatCurrency, formatDateTime } from "@/lib/utils";
import type { Order, SettingsMap } from "@/types";

export interface ReceiptMeta {
  shopName: string;
  shopAddress: string;
  shopPhone: string;
  social: string;
  footer: string;
  currency: string;
}

export function buildReceiptMeta(
  settings: SettingsMap | undefined,
): ReceiptMeta {
  return {
    shopName: settings?.receipt_header || settings?.shop_name || "Coffee Shop",
    shopAddress: settings?.receipt_address || settings?.shop_address || "",
    shopPhone: settings?.receipt_phone || settings?.shop_phone || "",
    social: settings?.receipt_social || "",
    footer: settings?.receipt_footer || "Thank you for your visit!",
    currency: settings?.currency_symbol || "$",
  };
}

export function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function row(
  label: string,
  value: string,
  opts: { bold?: boolean; large?: boolean; muted?: boolean } = {},
): string {
  const cls = ["row"];
  if (opts.bold) cls.push("bold");
  if (opts.large) cls.push("lg");
  if (opts.muted) cls.push("muted");

  return `<div class="${cls.join(" ")}"><span class="label">${esc(label)}</span><span class="value">${esc(value)}</span></div>`;
}

export function renderReceiptBody(order: Order, r: ReceiptMeta): string {
  const c = r.currency;

  const header = `
    <div class="center shop bold">${esc(r.shopName)}</div>
    ${r.shopAddress ? `<div class="center">${esc(r.shopAddress)}</div>` : ""}
    ${r.shopPhone ? `<div class="center">${esc(r.shopPhone)}</div>` : ""}
  `;

  const meta = [
    row("Order", `#${order.orderNumber}`, { bold: true }),
    row("Date", formatDateTime(order.createdAt)),
    row("Type", order.orderType.replace("_", " ")),
    order.customer
      ? row("Customer", order.customer.name)
      : row("Customer", "Guest"),
    order.user ? row("Cashier", order.user.firstName) : row("Cashier", "N/A"),
  ].join("");

  const items = order.items
    .map((item) => {
      const label = `${item.quantity}x ${item.productName}${item.variantName ? ` (${item.variantName})` : ""}`;
      const priceHtml = item.isComped
        ? `<span class="strike">${esc(formatCurrency(item.unitPrice * item.quantity, c))}</span> ${esc(formatCurrency(0, c))}`
        : esc(formatCurrency(item.subtotal, c));

      const lines = [
        `<div class="row item-row"><span class="label">${esc(label)}</span><span class="value">${priceHtml}</span></div>`,
      ];

      for (const opt of item.options) {
        lines.push(
          row(
            `  + ${opt.optionName}`,
            opt.priceAdjustment > 0
              ? `+${formatCurrency(opt.priceAdjustment, c)}`
              : "",
            { muted: true },
          ),
        );
      }

      return `<div class="item-block">${lines.join("")}</div>`;
    })
    .join("");

  const hasAdjustments = order.discountAmount > 0 || order.tipAmount > 0;

  const totals = [
    hasAdjustments ? row("Subtotal", formatCurrency(order.subtotal, c)) : "",
    order.discountAmount > 0
      ? row("Discount", `-${formatCurrency(order.discountAmount, c)}`)
      : "",
    order.tipAmount > 0 ? row("Tip", formatCurrency(order.tipAmount, c)) : "",
    hasAdjustments ? `<div class="dashed"></div>` : "",
    row("TOTAL", formatCurrency(order.total, c), { bold: true, large: true }),
  ].join("");

  let payments = "";
  if (order.payments.length > 0) {
    const lines = order.payments.map((p) =>
      row(p.method.toUpperCase(), formatCurrency(p.amountTendered, c)),
    );
    const changeTotal = order.payments.reduce((s, p) => s + p.changeGiven, 0);
    if (changeTotal > 0) {
      lines.push(row("Change", formatCurrency(changeTotal, c)));
    }
    payments = `<div class="dashed"></div><div>${lines.join("")}</div>`;
  }

  const footer = `
    <div class="dashed"></div>
    <div class="center">${esc(r.footer)}</div>
    ${r.social ? `<div class="center">${esc(r.social)}</div>` : ""}
  `;

  return `
    <div class="receipt">
      ${header}
      <div class="dashed"></div>
      ${meta}
      <div class="dashed"></div>
      <div class="items">${items}</div>
      <div class="dashed"></div>
      ${totals}
      ${payments}
      ${footer}
    </div>
  `;
}

export function renderReceiptDocument(order: Order, meta: ReceiptMeta): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt #${esc(order.orderNumber)}</title>
<style>
  /* Receipt-friendly print sizing */
  @page {
    size: 80mm auto;
    margin: 0;
  }

  * {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    padding: 0;
    width: 80mm;
    background: #fff;
    color: #000;
  }

  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
    line-height: 1.35;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .receipt {
    width: 72mm;
    margin: 0 auto;
    padding: 2mm 0;
  }

  .center {
    text-align: center;
  }

  .bold {
    font-weight: 700;
  }

  .lg {
    font-size: 14px;
  }

  .shop {
    font-size: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .muted {
    font-size: 11px;
  }

  .dashed {
    border-top: 1px dashed #000;
    margin: 6px 0;
    width: 100%;
  }

  .items > .item-block + .item-block {
    margin-top: 4px;
  }

  .row {
    display: flex;
    gap: 6px;
    width: 100%;
    align-items: flex-start;
  }

  .row .label {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .row .value {
    flex-shrink: 0;
    white-space: nowrap;
    text-align: right;
  }

  .item-row .label {
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .strike {
    text-decoration: line-through;
    color: #666;
  }

  @media print {
    html, body {
      width: 80mm;
      margin: 0;
      padding: 0;
    }

    .receipt {
      width: 72mm;
      margin: 0 auto;
      padding: 2mm 0;
    }
  }
</style>
</head>
<body>
${renderReceiptBody(order, meta)}
</body>
</html>`;
}
